import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Lightbulb, Shield, TrendingUp } from "lucide-react";

const PILLARS = [
  {
    icon: Lightbulb,
    title: "Innovation",
    desc: "Pushing the boundaries of what's possible with AI video and image generation.",
  },
  {
    icon: Shield,
    title: "Reliability",
    desc: "Enterprise-grade architecture built for high-volume, mission-critical operations.",
  },
  {
    icon: TrendingUp,
    title: "Impact",
    desc: "Focused on driving measurable growth for the modern entrepreneur.",
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export default function AboutUs() {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="about-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="about-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="about-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            {...fade()}
            className="text-xs font-bold uppercase tracking-[0.25em] mb-4"
            style={{ color: "#FF5A36" }}
          >
            About Us
          </motion.p>
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#1A1A1A" }}
            data-testid="about-headline"
          >
            About Prysh AI Labs
          </motion.h1>
          <motion.p
            {...fade(0.2)}
            className="mt-6 text-base lg:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: "rgba(26,26,26,0.55)" }}
          >
            At Prysh AI Labs, we are redefining the intersection of e-commerce and generative media. Our mission is to empower brands and sellers by transforming static product listings into high-conversion, cinematic video advertisements in seconds.
          </motion.p>
        </div>
      </section>

      {/* ── WHO WE ARE ── */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fade()}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6" style={{ color: "#1A1A1A" }}>
              Who We Are
            </h2>
            <div className="space-y-5 text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
              <p>
                Founded by a team of Computer Science engineers and industry veterans with over a decade of expertise, Prysh AI was born out of a simple observation: in the fast-paced world of digital commerce, traditional video production is too slow, too expensive, and too rigid.
              </p>
              <p>
                Since our official incorporation in January 2026, we have stayed committed to one goal — building the world's most intuitive AI-driven video engine for the global marketplace.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fade()}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6" style={{ color: "#1A1A1A" }}>
              What We Do
            </h2>
            <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(26,26,26,0.6)" }}>
              We develop cutting-edge AI infrastructure designed specifically for the e-commerce ecosystem. Our platform, Prysh.ai, leverages advanced latent diffusion models and proprietary creative frameworks to:
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                title: "Automate Creative Workflows",
                desc: "Generate professional-grade video ads directly from product URLs or images.",
              },
              {
                title: "Scale at Speed",
                desc: "Enable platforms and individual sellers to produce thousands of unique video variations instantly.",
              },
              {
                title: "Optimize for Conversion",
                desc: "Bridge the gap between technical AI innovation and real-world ROI for retailers.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fade(i * 0.1)}
                className="bg-white rounded-2xl p-6 border"
                style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
              >
                <h3 className="font-display text-lg font-semibold mb-2" style={{ color: "#1A1A1A" }}>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(26,26,26,0.55)" }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fade()}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6" style={{ color: "#1A1A1A" }}>
              Our Vision
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
              We believe the future of shopping is immersive. Whether it's helping a boutique brand launch its first social media campaign or powering large-scale video automation for global enterprises, we are here to ensure that every product has a story worth watching.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── KEY PILLARS ── */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            {...fade()}
            className="font-display text-2xl sm:text-3xl font-bold mb-10 text-center"
            style={{ color: "#1A1A1A" }}
          >
            Key Pillars of Our Laboratory
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((pillar, i) => (
              <motion.div
                key={i}
                {...fade(i * 0.1)}
                className="bg-white rounded-2xl p-8 text-center border"
                style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: "rgba(255,90,54,0.1)" }}
                >
                  <pillar.icon className="w-6 h-6" style={{ color: "#FF5A36" }} />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2" style={{ color: "#1A1A1A" }}>
                  {pillar.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(26,26,26,0.55)" }}>{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-6 md:px-12 text-center">
        <motion.div {...fade()} className="max-w-2xl mx-auto">
          <p
            className="font-display text-xl sm:text-2xl font-bold mb-3"
            style={{ color: "#1A1A1A" }}
          >
            Prysh AI Labs
          </p>
          <p className="text-sm mb-8 italic" style={{ color: "rgba(26,26,26,0.45)" }}>
            Engineering the future of digital storefronts.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book-demo">
              <Button
                className="rounded-full px-8 py-6 text-base font-medium border-0 gap-2 transition-shadow hover:shadow-[0_8px_30px_rgba(255,90,54,0.25)]"
                style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
                data-testid="about-cta-contact"
              >
                Book a Demo — Get 1 Free Ad
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/signup">
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-medium border"
                style={{ borderColor: "rgba(0,0,0,0.12)", color: "#1A1A1A" }}
                data-testid="about-cta-signup"
              >
                Get Started Free
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/pricing" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }} data-testid="about-footer-pricing">Pricing</Link>
            <Link to="/book-demo" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }} data-testid="about-footer-contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
